// src/components/LogsPreview/LogsPreview.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { Box, Typography, Fab } from "@material-ui/core";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";

const SCROLL_BOTTOM_THRESHOLD_PX = 15;

export default function LogsPreview({ stdout, height = 340 }) {
  const stdoutRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const [hasNewOutput, setHasNewOutput] = useState(false);

  const normalizedStdout = useMemo(() => stdout || "", [stdout]);

  const scrollToBottom = () => {
    const el = stdoutRef.current;
    if (!el) return;

    el.scrollTop = el.scrollHeight;
    isAtBottomRef.current = true;
    setHasNewOutput(false);
  };

  const handleScroll = () => {
    const el = stdoutRef.current;
    if (!el) return;

    const distanceToBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    const atBottom = distanceToBottom < SCROLL_BOTTOM_THRESHOLD_PX;

    isAtBottomRef.current = atBottom;
    if (atBottom) setHasNewOutput(false);
  };

  // stdout cambia
  useEffect(() => {
    const el = stdoutRef.current;
    if (!el) return;

    if (isAtBottomRef.current) {
      requestAnimationFrame(scrollToBottom);
    } else {
      setHasNewOutput(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [normalizedStdout]);

  // al montar (abrir modal): empezar abajo siempre
  useEffect(() => {
    requestAnimationFrame(scrollToBottom);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box
      style={{
        position: "relative",
        borderRadius: 12,
        background: "#0f172a",
        border: "1px solid rgba(255,255,255,0.08)",
        overflow: "hidden",
      }}
    >
      {/* LOGS */}
      <Box
        ref={stdoutRef}
        onScroll={handleScroll}
        style={{
          maxHeight: height,
          overflowY: "auto",
          whiteSpace: "pre-wrap",
          fontFamily:
            "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace",
          fontSize: 12,
          lineHeight: 1.45,
          padding: 12,
          color: "#e2e8f0",
        }}
      >
        {normalizedStdout ? (
          <Typography component="div" style={{ whiteSpace: "pre-wrap" }}>
            {normalizedStdout}
          </Typography>
        ) : (
          <Typography style={{ opacity: 0.7 }}>—</Typography>
        )}
      </Box>

      {/* FAB flotante */}
      {hasNewOutput && (
        <Box
          style={{
            position: "absolute",
            left: "50%",
            bottom: 14,
            transform: "translateX(-50%)",
            zIndex: 3,

            /* glass container */
            padding: 6,
            borderRadius: 999,
            background: "rgba(15, 23, 42, 0.6)",
            border: "1px solid rgba(148,163,184,0.25)",
            backdropFilter: "blur(6px)",
            WebkitBackdropFilter: "blur(6px)",
            boxShadow: "0 10px 28px rgba(0,0,0,0.45)",

            /* bounce animation */
            animation: "logs-fab-bounce 1.6s infinite",
          }}
        >
          <Fab
            size="small"
            onClick={scrollToBottom}
            style={{
              background:
                "linear-gradient(135deg, #38bdf8 0%, #0ea5e9 100%)",
              color: "#020617",
              boxShadow:
                "0 0 0 1px rgba(56,189,248,0.35), 0 6px 16px rgba(14,165,233,0.6)",
            }}
            aria-label="Ir al final"
          >
            <ArrowDownwardIcon />
          </Fab>
        </Box>
      )}

      {/* Animación inline (scoped) */}
      <style>
        {`
          @keyframes logs-fab-bounce {
            0%, 100% {
              transform: translateX(-50%) translateY(0);
            }
            50% {
              transform: translateX(-50%) translateY(-6px);
            }
          }
        `}
      </style>
    </Box>
  );
}
